import React, { useState, useEffect } from 'react';
import { BASE } from '../utils/api';
import { Gift, Plus, Trash2, Users, Clock, RefreshCw, DollarSign } from 'lucide-react';

interface PromoCode {
  id: number;
  code: string;
  amount: number;
  max_claims: number;
  total_claimed: number;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

const GlobalPromoCodes: React.FC = () => {
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [amount, setAmount] = useState('');
  const [maxClaims, setMaxClaims] = useState('');
  const [expiresInHours, setExpiresInHours] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/promocode/all`);
      const data = await res.json();
      if (data.success) setCodes(data.codes);
    } catch (err) {
      console.error('Error fetching promo codes:', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchCodes(); }, []);

  const handleCreate = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setMessage('Please enter a valid amount');
      setMessageType('error');
      return;
    }

    setCreating(true);
    setMessage('');
    try {
      const res = await fetch(`${BASE}/promocode/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(amount),
          max_claims: parseInt(maxClaims) || 1,
          expires_in_hours: parseInt(expiresInHours) || null
        })
      });
      const data = await res.json();
      if (data.success) {
        setMessage(`Code created: ${data.promoCode.code}`);
        setMessageType('success');
        setAmount('');
        setMaxClaims('');
        setExpiresInHours('');
        fetchCodes();
      } else {
        setMessage(data.error || 'Failed to create code');
        setMessageType('error');
      }
    } catch (err) {
      setMessage('Failed to create promo code');
      setMessageType('error');
    }
    setCreating(false);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Deactivate this promo code?')) return;
    try {
      await fetch(`${BASE}/promocode/${id}`, { method: 'DELETE' });
      fetchCodes();
    } catch (err) {
      console.error('Error deleting code:', err);
    }
  };

  const formatDate = (d: string | null) => {
    if (!d) return 'Never';
    return new Date(d).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Create Form */}
      <div className="bg-[#232626] rounded-xl border border-[#2f3536] p-6">
        <h3 className="text-lg font-semibold text-[#e2e8f0] mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-[#25ee89]" />
          Create Global Promo Code
        </h3>
        <p className="text-sm text-[#94a3b8] mb-4">
          Create a code that any user can claim. </p>
        {message && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${
            messageType === 'success' 
              ? 'bg-[#1f3b2a] text-[#25ee89] border border-[#25ee89]/20' 
              : 'bg-[#3b1f1f] text-red-400 border border-red-400/20'
          }`}>
            {message}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-[#94a3b8] mb-1">Amount</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className="h-4 w-4 text-[#94a3b8]" />
              </div>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g., 50"
                className="w-full pl-9 pr-3 py-2.5 bg-[#1a1d1e] border border-[#2f3536] rounded-lg text-[#e2e8f0] focus:outline-none focus:ring-2 focus:ring-[#25ee89] placeholder-[#64748b]"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-[#94a3b8] mb-1">Max Claims</label>
            <input
              type="number"
              value={maxClaims}
              onChange={(e) => setMaxClaims(e.target.value)}
              placeholder="e.g., 100"
              className="w-full px-3 py-2.5 bg-[#1a1d1e] border border-[#2f3536] rounded-lg text-[#e2e8f0] focus:outline-none focus:ring-2 focus:ring-[#25ee89] placeholder-[#64748b]"
            />
          </div>
          <div>
            <label className="block text-sm text-[#94a3b8] mb-1">Valid For (hours)</label>
            <input
              type="number"
              value={expiresInHours}
              onChange={(e) => setExpiresInHours(e.target.value)}
              placeholder="Leave empty = no expiry"
              className="w-full px-3 py-2.5 bg-[#1a1d1e] border border-[#2f3536] rounded-lg text-[#e2e8f0] focus:outline-none focus:ring-2 focus:ring-[#25ee89] placeholder-[#64748b]"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleCreate}
              disabled={creating}
              className="w-full px-4 py-2.5 bg-[#25ee89] text-[#0d1117] font-semibold rounded-lg hover:bg-[#1dbf6f] transition-colors disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create Code'}
            </button>
          </div>
        </div>
      </div>

      {/* Codes List */}
      <div className="bg-[#232626] rounded-xl border border-[#2f3536] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#2f3536] flex justify-between items-center">
          <h3 className="text-lg font-semibold text-[#e2e8f0] flex items-center gap-2">
            <Gift className="w-5 h-5 text-[#94a3b8]" />
            All Promo Codes
            <span className="text-xs bg-[#2f3536] text-[#94a3b8] px-2 py-0.5 rounded-full">
              {codes.length}
            </span>
          </h3>
          <button onClick={fetchCodes} disabled={loading} className="text-[#94a3b8] hover:text-[#e2e8f0]">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-[#94a3b8]">Loading...</div>
        ) : codes.length === 0 ? (
          <div className="p-8 text-center text-[#94a3b8]">No promo codes created yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-[#1a1d1e]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#94a3b8] uppercase">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#94a3b8] uppercase">Amount ($)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#94a3b8] uppercase">Claims</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#94a3b8] uppercase">Expires</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#94a3b8] uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#94a3b8] uppercase">Created</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[#94a3b8] uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2f3536]">
                {codes.map((code) => (
                  <tr key={code.id} className="hover:bg-[#1a1d1e]">
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm bg-[#1a1d1e] text-[#25ee89] px-2 py-1 rounded border border-[#25ee89]/20">
                        {code.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-[#e2e8f0] font-semibold">{parseFloat(code.amount as any).toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <span className="text-[#e2e8f0]">{code.total_claimed}</span>
                      <span className="text-[#94a3b8]"> / {code.max_claims}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#94a3b8]">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(code.expires_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {code.is_active ? (
                        <span className="text-xs bg-[#1f3b2a] text-[#25ee89] px-2 py-1 rounded-full">Active</span>
                      ) : (
                        <span className="text-xs bg-[#3b1f1f] text-red-400 px-2 py-1 rounded-full">Inactive</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#94a3b8]">{formatDate(code.created_at)}</td>
                    <td className="px-6 py-4 text-right">
                      {code.is_active && (
                        <button
                          onClick={() => handleDelete(code.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalPromoCodes;
